import React, { useEffect, useState, useRef } from "react";
import { motion } from 'motion/react';
import { Car, ArrowRight, CheckCircle2, Menu, X, Clock, AlertTriangle } from 'lucide-react';
import { message, Slider } from 'antd';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionActions } from "../../actions/subscriptionActions";
import { subscription_plan, FREE_TRIAL_DAYS } from "../../actions/constants/subscriptionConstants";
import { openRazorpayCheckout, openRazorpayOrderCheckout } from "../../services/helper";
import { Footer } from "../../components/landingSections";
import { Navbar } from "../../components/landingHero";
import PublicChatbot from "../../components/chatbot/publicChatbot";
import { userActions } from "../../actions/userActions";


const Pricing: React.FC = () => {

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = React.useState<'monthly' | 'yearly'>('monthly');
  const [studentCount, setStudentCount] = React.useState(500);
  const [selectedPlanIndex, setSelectedPlanIndex] = React.useState(1);

  const subscriptionPlan = useSelector((state: any) => state.getSubscriptionPlanReducer);
  const createSubscription = useSelector((state: any) => state.createSubscriptionReducer);
  const getPlans = useSelector((state: any) => state.getAllPlansReducer);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const lastCreatedPlanRef = useRef<string | null>(null);
  const selectedPlanRef = useRef<any | null>(null);
  const handledSubscriptionRef = useRef<string | null>(null);
  const autoTriggerProcessedRef = useRef<boolean>(false);
  const profile = useSelector((state: any) => state.getProfileReducer);
  const currentSubscription = profile?.profileData?.currentSubscription;
  const navigate = useNavigate();
  const isLandingRoute = location?.pathname === '/';
  const isPricingRoute = location?.pathname === '/pricing';

  const isManualScrollRef = React.useRef(false);
  const manualScrollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);


  const loadingPlanId = createSubscription?.isLoading ? selectedPlanRef.current?._id ?? pendingPlanId : null;
  const billingActive = currentSubscription?.plan?.billingCycle;
  const status = currentSubscription?.status;
  const isSameBilling = billingActive === billingCycle;

  const currentPlanName = currentSubscription?.plan?.name;
  const isFreeTrial = currentPlanName?.toLowerCase().includes('free');
  const currentPlanPrice = currentSubscription?.plan?.price ?? currentSubscription?.plan?.basePrice ?? 0;
  const subscriptionEndDate = currentSubscription?.endDate;

  const daysRemaining = React.useMemo(() => {
    if (!subscriptionEndDate) return null;
    const end = new Date(subscriptionEndDate);
    const now = new Date();
    const msInDay = 1000 * 60 * 60 * 24;
    return Math.ceil((end.getTime() - now.getTime()) / msInDay);
  }, [subscriptionEndDate]);

  const isExpired = daysRemaining !== null && daysRemaining <= 0;
  const isExpiringSoon = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= FREE_TRIAL_DAYS;
  const hasActiveSubscription = (status === 'active' || status === 'scheduled') && !isExpired;
  const hasDifferentBillingCycle = hasActiveSubscription && billingActive && billingActive !== billingCycle;
  const freePlan = React.useMemo(() => {
    if (!getPlans?.isSuccess || !getPlans?.data) return null;
    return getPlans.data.find((p: any) => p.name?.toLowerCase().includes('free'));
  }, [getPlans]);

  const filteredPlans = React.useMemo(() => {
    if (!getPlans?.isSuccess || !getPlans?.data) return [];
    const paidPlans = getPlans.data.filter((p: any) =>
      p.billingCycle === billingCycle &&
      !p.name?.toLowerCase().includes('free')
    );
    if (freePlan) {
      return [freePlan, ...paidPlans];
    }
    return paidPlans;
  }, [getPlans, billingCycle, freePlan]);

  useEffect(() => {
    if (filteredPlans.length > 0 && selectedPlanIndex >= filteredPlans.length) {
      setSelectedPlanIndex(Math.min(selectedPlanIndex, filteredPlans.length - 1));
    }
  }, [filteredPlans, selectedPlanIndex]);


  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    if (data) {
      const profileData = JSON.parse(String(data));
      dispatch(userActions.getProfileRequest({ id: profileData.id }));
    }
    dispatch(subscriptionActions.getAllPlansRequest());
  }, [])

  useEffect(() => {
    const planParam = searchParams.get('plan');
    const billingParam = searchParams.get('billing');

    if (planParam && billingParam && !autoTriggerProcessedRef.current) {
      const authData = localStorage.getItem('AUTHDATA');
      if (authData) {
        autoTriggerProcessedRef.current = true;

        if (billingParam === 'yearly' || billingParam === 'monthly') {
          setBillingCycle(billingParam);
        }

        let planToTrigger = undefined;
        if (getPlans?.isSuccess && getPlans?.data) {
          planToTrigger = getPlans.data.find((p: any) => (p._id === planParam || p.id === planParam || p.name === planParam));
        }

        if (planToTrigger) {
          setTimeout(() => {
            // when using DB plan object, pass full plan; otherwise, pass the fallback
            handleUpgrade(planToTrigger);
            setSearchParams({});
          }, 500);
        }
      }
    }
  }, [searchParams, setSearchParams, getPlans]);

  const STUDENT_STOPS = [50, 250, 500, 750, 1000, 2000];
  const studentMarks: Record<number, React.ReactNode> = {
    0: '50',
    1: '250',
    2: '500',
    3: '750',
    4: '1000',
    5: 'Unlimited',
  };

  const sliderValue = React.useMemo(() => {
    const idx = STUDENT_STOPS.indexOf(studentCount);
    return idx === -1 ? 2 : idx;
  }, [studentCount]);

  const handleSliderChange = (position: number) => {
    const val = STUDENT_STOPS[position] ?? 500;
    setStudentCount(val);

    const planCount = filteredPlans.length;
    if (planCount === 0) return;

    if (planCount === 4) {
      if (val <= 250) setSelectedPlanIndex(1);
      else if (val <= 1000) setSelectedPlanIndex(2);
      else setSelectedPlanIndex(3);
    } else if (planCount === 3) {
      if (val <= 250) setSelectedPlanIndex(0);
      else if (val <= 1000) setSelectedPlanIndex(1);
      else setSelectedPlanIndex(2);
    } else if (planCount === 2) {
      if (val <= 500) setSelectedPlanIndex(0);
      else setSelectedPlanIndex(1);
    } else if (planCount === 1) {
      setSelectedPlanIndex(0);
    }
  };

  const handlePlanClick = (index: number) => {
    setSelectedPlanIndex(index);
    const planCount = filteredPlans.length;

    if (planCount === 4) {
      if (index === 0) setStudentCount(50);
      else if (index === 1) setStudentCount(250);
      else if (index === 2) setStudentCount(750);
      else setStudentCount(2000);
    } else if (planCount === 3) {
      if (index === 0) setStudentCount(250);
      else if (index === 1) setStudentCount(750);
      else setStudentCount(2000);
    } else if (planCount === 2) {
      if (index === 0) setStudentCount(500);
      else setStudentCount(2000);
    } else if (planCount === 1) {
      setStudentCount(750);
    }
  };

  // Helper function to resolve plan name to subscription key
  const resolveSubscriptionKey = (planName: string) => {
    let key;
    switch (planName) {
      case 'Professional':
        key = subscription_plan.pro;
        break;
      case 'Enterprise':
        key = subscription_plan.ultimate;
        break;
      case 'Basic':
      case 'Starter':
      default:
        key = subscription_plan.free;
        break;
    }
    return key;
  };



  const handleUpgrade = (plan: any) => {

    const authData = localStorage.getItem('AUTHDATA');
    const isAuthenticated = !!authData;

    if (!plan) {
      return;
    }

    // Check if user is trying to activate free trial when they already have an active subscription
    const isFreePlanCard = plan.name?.toLowerCase().includes('free');
    if (isFreePlanCard && hasActiveSubscription) {
      message.warning({
        content: isFreeTrial
          ? 'You already have an active Free Trial. Please upgrade to a paid plan.'
          : 'You already have an active subscription. Free Trial is not available.',
        key: 'plan-upgrade',
        duration: 5
      });
      return;
    }

    if (hasActiveSubscription && currentPlanName === plan.name) {
      message.warning({
        content: `You already have an active ${currentPlanName || ''} subscription. If you want to change plans, please proceed to upgrade.`,
        key: 'plan-upgrade',
        duration: 5
      });
      return;
    }

    // Prevent downgrade: check if new plan price is lower than current plan price
    const newPlanPrice = plan.price ?? plan.basePrice ?? 0;
    if (hasActiveSubscription && !isFreeTrial && newPlanPrice < currentPlanPrice && newPlanPrice > 0) {
      message.error({
        content: `You cannot downgrade from ₹${currentPlanPrice} plan to ₹${newPlanPrice} plan. Please choose a plan equal to or higher than your current plan.`,
        key: 'plan-upgrade',
        duration: 6
      });
      return;
    }

    if (!isAuthenticated) {
      const billing = billingCycle;
      window.location.href = `/login?redirect=pricing&plan=${encodeURIComponent(plan?.name ?? plan?.name)}&billing=${billing}`;
      return;
    }

    const planId = plan._id ?? plan.id;
    if (!planId) {
      message.error({ content: 'Invalid plan selection', key: 'plan-upgrade' });
      return;
    }

    setPendingPlanId(planId);
    selectedPlanRef.current = plan;
    handledSubscriptionRef.current = null;

    let userId = null;
    if (authData) {
      try {
        const parsedAuth = JSON.parse(authData);
        userId = parsedAuth?.id || parsedAuth?.userId || parsedAuth?._id;
      } catch (e) { }
    }

    if (!userId) {
      message.error({ content: 'User not authenticated', key: 'plan-upgrade' });
      return;
    }

    lastCreatedPlanRef.current = planId;
    const loadingMessage = (plan.price ?? plan.basePrice ?? 0) === 0
      ? `Activating your ${plan.name} plan...`
      : `Connecting you to the ${plan.name} plan...`;
    message.loading({ content: loadingMessage, key: 'plan-upgrade' });

    // Show success message for the selected plan
    const successMessage = (plan.price ?? plan.basePrice ?? 0) === 0
      ? `${plan.name} plan setup in progress`
      : `${plan.name} (${plan.billingCycle}) plan setup in progress`;
    setTimeout(() => {
      message.success({ content: successMessage, key: 'plan-upgrade', duration: 2 });
    }, 500);

    dispatch(subscriptionActions.createSubscriptionRequest({ userId, planId }));
  };

  useEffect(() => {

    if (!createSubscription?.isSuccess || !createSubscription?.data) {
      return;
    }

    const isFreePlan = createSubscription.data.amount === 0;

    if (isFreePlan) {
      const planDetails = selectedPlanRef.current ?? (getPlans?.data?.find((p: any) => (p.price === 0 || p.basePrice === 0)));
      message.success({ content: `${planDetails?.name ?? 'Starter'} plan activated! Redirecting to dashboard...`, key: 'plan-upgrade' });
      selectedPlanRef.current = null;
      handledSubscriptionRef.current = null;
      setPendingPlanId(null);
      // Navigate to dashboard after free trial activation
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
      return;
    }

    const checkoutType = createSubscription?.data?.checkoutType;
    const checkoutId = checkoutType === "order" ? createSubscription.data.orderId : createSubscription.data.subscriptionId;

    if (!checkoutId) {
      return;
    }

    if (handledSubscriptionRef.current === checkoutId) {
      return;
    }
    handledSubscriptionRef.current = checkoutId;
    if (checkoutType === "order") {
      const amount = createSubscription.data.amount || createSubscription.data.totalAmount || 0;
      openRazorpayOrderCheckout(checkoutId, amount);
    } else {

      openRazorpayCheckout(checkoutId);
    }

  }, [createSubscription?.isSuccess, createSubscription?.data]);



  useEffect(() => {
    if (createSubscription?.isError && createSubscription?.error) {
      message.error({
        content: createSubscription.error?.message || createSubscription.error || 'Failed to create subscription',
        key: 'plan-upgrade'
      });
      setPendingPlanId(null);
      selectedPlanRef.current = null;
    }
  }, [createSubscription?.isError, createSubscription?.error]);


  useEffect(() => {
    if (subscriptionPlan?.isError && subscriptionPlan?.error) {
      message.error({
        content: subscriptionPlan.error?.message || subscriptionPlan.error || 'Failed to fetch plan details',
        key: 'plan-upgrade'
      });
      setPendingPlanId(null);
      selectedPlanRef.current = null;
    }
  }, [subscriptionPlan?.isError, subscriptionPlan?.error]);

  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    setActiveSection(sectionId);
    isManualScrollRef.current = true;
    if (manualScrollTimerRef.current) clearTimeout(manualScrollTimerRef.current);
    manualScrollTimerRef.current = setTimeout(() => {
      isManualScrollRef.current = false;
    }, 1000);

    if (isLandingRoute) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    navigate('/');
    let attempts = 0;
    const maxAttempts = 40;
    const tryScroll = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        return;
      }
      attempts += 1;
      if (attempts <= maxAttempts) {
        setTimeout(tryScroll, 100);
      }
    };
    setTimeout(tryScroll, 120);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Subscription / Free Trial Expired Banner */}
          {isExpired && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-900">
                    {isFreeTrial ? 'Your Free Trial Has Expired' : 'Your Subscription Has Expired'}
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {isFreeTrial
                      ? 'Upgrade now to continue using all features and keep your data.'
                      : 'Renew now to restore access to all features and your data.'}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Free Trial Expiring Soon Banner */}
          {isFreeTrial && isExpiringSoon && !isExpired && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-900">
                    Free Trial: {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Upgrade before your trial ends to avoid service interruption.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Active Paid Subscription Banner */}
          {hasActiveSubscription && !isFreeTrial && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-green-900">
                    Active Subscription: {currentPlanName || 'Current Plan'}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    You currently have an active subscription.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          <div className="text-center mb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6">
                Pricing that <span className="text-brand-primary">grows</span> with you.
              </h1>
              <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                No complex tiers. Just simple, scalable pricing based on your school's size.
              </p>
            </motion.div>
          </div>

          <div className="flex flex-col items-center gap-12 mb-20">
            {/* <div className="flex items-center gap-4 bg-slate-100 p-1.5 rounded-full border border-slate-200">
              <button
                onClick={() => setBillingCycle('monthly')}
               
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-white text-brand-primary' 
                    : 'text-slate-500 hover:text-slate-700'
                } ${hasActiveSubscription ? 'opacity-80' : ''}`}
                title={hasActiveSubscription ? `You have an active ${billingActive} subscription` : ''}
              >
                Monthly
              </button>
              <button 
                onClick={() =>  setBillingCycle('yearly')} 
                
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly' 
                    ? 'bg-white text-brand-primary' 
                    : 'text-slate-500 hover:text-slate-700'
                } ${hasActiveSubscription ? 'opacity-80' : ''}`}
                title={hasActiveSubscription ? `You have an active ${billingActive} subscription` : ''}
              >
                Yearly
                <span className="bg-brand-accent/10 text-brand-accent text-[10px] px-2 py-0.5 rounded-full">Save 20%</span>
              </button>
            </div> */}

            <div className="w-full max-w-2xl px-8 md:max-w-3xl lg:max-w-4xl">
              <div className="flex justify-between mb-4 text-sm font-bold text-slate-400 uppercase tracking-wider">
                <span>Student Capacity</span>
                <span className="text-brand-primary">
                  {studentCount === 2000 ? 'Unlimited Students' : `${studentCount} Students`}
                </span>
              </div>

              <Slider
                min={0}
                max={STUDENT_STOPS.length - 1}
                step={1}
                marks={studentMarks}
                value={sliderValue}
                onChange={(val) => handleSliderChange(val as number)}
                tooltip={{ open: false }}
                included={false}
                className="w-full pricing-slider"
                railStyle={{ backgroundColor: '#e2e8f0', height: 12, borderRadius: 20 }}
                handleStyle={{
                  borderColor: '#4f46e5',
                  marginTop: -3,
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(79,70,229,0.12)',
                }}
              />
              <style>{`
                .pricing-slider .ant-slider-dot {
                  display: none !important;
                }
              `}</style>

            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 items-start">
            {filteredPlans?.map((plan: any, index: any) => {
              const isSelected = index === selectedPlanIndex;
              const isPlanActive = hasActiveSubscription && isSameBilling && currentPlanName === plan.name;
              const isFreePlanCard = plan.name?.toLowerCase().includes('free');
              const isCurrentFreeTrial = isFreeTrial && isFreePlanCard;

              const planPrice = plan.price ?? plan.basePrice ?? 0;
              const isDowngrade = hasActiveSubscription && isSameBilling && !isFreeTrial && planPrice < currentPlanPrice && planPrice > 0;
              const isFreeTrialDisabled =
                isFreePlanCard && !isCurrentFreeTrial && (isExpired || hasActiveSubscription);
              const isDifferentBillingCycle = hasDifferentBillingCycle && !isFreePlanCard;

              const isCardDisabled =
                isPlanActive || isCurrentFreeTrial || isDowngrade || isFreeTrialDisabled || isDifferentBillingCycle;

              return (
                <motion.div key={plan._id ?? index}
                  onClick={() => !isCardDisabled && handlePlanClick(index)}
                  whileHover={isCardDisabled ? undefined : "hover"}
                  initial="initial"
                  animate={isSelected ? "selected" : "initial"}
                  variants={{
                    initial: { scale: 1, opacity: 1, y: 0 },
                    hover: { scale: 1, opacity: 1, y: -5 }
                  }}
                  className={`relative p-6 md:p-7 h-[520px] rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col ${isPlanActive || isCurrentFreeTrial || isDowngrade || isFreeTrialDisabled || isDifferentBillingCycle ? 'opacity-70 cursor-not-allowed' : ''
                    } ${isSelected
                      ? 'border-brand-primary bg-slate-900 text-white shadow-2xl'
                      : 'border-slate-200 bg-white text-slate-900 hover:border-brand-primary/40 hover:shadow-lg'
                    }`}
                >
                  <motion.div
                    variants={{
                      initial: { opacity: 0 },
                      hover: { opacity: 1 },
                      selected: { opacity: 1 }
                    }}
                    className="absolute -inset-[2px] rounded-2xl pointer-events-none overflow-hidden z-0"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="absolute -inset-[100%] bg-[conic-gradient(from_0deg,transparent_0deg,var(--primary-color)_180deg,transparent_360deg)] opacity-30"
                    />
                  </motion.div>

                  <div className="relative z-10 flex flex-col h-full">
                    {isDowngrade && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-red-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        Downgrade Not Allowed
                      </div>
                    )}
                    {!isDowngrade && isDifferentBillingCycle && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        {billingActive === 'yearly' ? 'Monthly Not Available' : 'Yearly Not Available'}
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && isFreeTrialDisabled && !isCurrentFreeTrial && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        Not Available
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && isCurrentFreeTrial && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        Current Trial
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && !isFreeTrialDisabled && isPlanActive && !isCurrentFreeTrial && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-green-600 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        Active Plan
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && !isFreeTrialDisabled && isFreePlanCard && !isCurrentFreeTrial && !isPlanActive && billingCycle === 'monthly' && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        7 Days Free
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && !isFreeTrialDisabled && isFreePlanCard && !isCurrentFreeTrial && isPlanActive && billingCycle === 'yearly' && (
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        Not Available
                      </div>
                    )}
                    {!isDowngrade && !isDifferentBillingCycle && !isFreeTrialDisabled && isSelected && !isPlanActive && !isFreePlanCard && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-primary text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-md whitespace-nowrap">
                        {index === 2 ? 'Most Popular' : 'Selected Plan'}
                      </div>
                    )}

                    <div className="mb-3 mt-2">
                      <h3 className={`text-xl font-bold mb-1 ${isSelected ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                      <p className={`text-xs ${isSelected ? 'text-slate-400' : 'text-slate-500'}`}>{plan.description ?? plan.content?.[0] ?? ''}</p>
                    </div>


                    {isFreePlanCard ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold tracking-tight">Free</span>
                        <span className={`text-sm font-medium ${isSelected ? 'text-slate-400' : 'text-slate-500'}`}>
                          for {FREE_TRIAL_DAYS} days
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold">₹</span>
                        <span className="text-4xl font-bold tracking-tight">
                          {plan.price ?? plan.basePrice ?? 0}
                        </span>
                        <span className={`text-sm font-medium ${isSelected ? 'text-slate-400' : 'text-slate-500'}`}>
                          /{billingCycle === 'yearly' ? 'yr' : 'mo'}
                        </span>
                      </div>
                    )}


                    <div className={`h-px w-full mb-5 ${isSelected ? 'bg-slate-700' : 'bg-slate-200'}`} />

                    <ul className="space-y-3 mb-6 flex-grow">
                      {(plan.content || plan.features || []).map((feature: any, fIndex: number) => (
                        <li key={fIndex} className="flex items-start gap-2 text-sm">
                          <div className={`p-0.5 rounded-full flex-shrink-0 mt-0.5 ${isSelected ? 'text-emerald-400' : 'text-brand-primary'}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </div>
                          <span className={`${isSelected ? 'text-slate-300' : 'text-slate-600'}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isCardDisabled) handleUpgrade(plan);
                      }}
                      disabled={isCardDisabled || loadingPlanId === (plan._id ?? plan.id)}
                      className={`mt-auto w-full py-3.5 rounded-xl font-semibold text-sm transition-all group flex items-center justify-center gap-2 ${isSelected
                        ? 'bg-white !text-black hover:bg-slate-100'
                        : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                        } ${(loadingPlanId === (plan._id ?? plan.id) || isPlanActive || isCurrentFreeTrial || isDowngrade || isFreeTrialDisabled || isDifferentBillingCycle) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loadingPlanId === (plan._id ?? plan.id)
                        ? 'Processing...'
                        : isDowngrade
                          ? 'Cannot Downgrade'
                          : isDifferentBillingCycle
                            ? `${billingActive === 'yearly' ? 'Monthly' : 'Yearly'} Not Available`
                            : isFreeTrialDisabled
                              ? 'Not Available'
                              : isCurrentFreeTrial
                                ? 'Active Trial'
                                : isPlanActive
                                  ? 'Current Plan'
                                  : isFreePlanCard
                                    ? (billingCycle === 'yearly' ? 'Not Available' : 'Start Free Trial')
                                    : (isSelected ? 'Get Started' : 'Choose Plan')}
                      {loadingPlanId !== (plan._id ?? plan.id) && !isPlanActive && !isCurrentFreeTrial && !isDowngrade && !isFreeTrialDisabled && !isDifferentBillingCycle && !(isFreePlanCard && billingCycle === 'yearly') && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                    </button>
                  </div>

                  <motion.div
                    variants={{
                      initial: { opacity: 0, scale: 0.8 },
                      hover: { opacity: 0.1, scale: 1 },
                      selected: { opacity: 0.15, scale: 1.1 }
                    }}
                    className="absolute inset-0 rounded-2xl pointer-events-none bg-brand-primary blur-[60px] z-0"
                  />
                </motion.div>
              );
            })}
          </div>

          <div className="mt-20 text-center">
            <p className="text-slate-600 mb-8">Need a custom plan for your organization?</p>
            <a className="inline-flex items-center gap-2 text-brand-primary hover:text-blue-700 font-bold text-sm transition-colors">
              <span className="cursor-pointer" onClick={() => scrollToSection('contact')}>Contact our sales team</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <PublicChatbot />
    </div>
  );
};

export default Pricing;
